import React from "react";
import { connect } from "react-redux";
import language from "../../../language";

import {
  fetchAndUpdateCurrentInventoriesStockStatus,
  fetchCurrentInventoriesPerProductType,
} from "../../../utils/queries/production/inventoryQuery";
import { getStorage } from "../../../utils/storage";
import { hasNationalInventory } from "../../../utils/handleAccessRoles";
import icons from "../../../constants/icons";
import { Loading } from "../../Loader";
import CardCount from "../../CardCount/CardCount";
import CardHeader from "../../Header/CardHeader";
import "./styles.css";
import { Empty } from "../../Empty";
import { Modal } from "../../Modal";
import { Select } from "../../Input";
import { Button } from "../../Button";
import toastMessage from "../../../utils/toastMessage";
import api from "../../../utils/api";
import ViewProducts from "./ViewProducts";

// Stands for every hospital at once rather than one of them, so the filter can
// say out loud what an empty filter already did silently.
const ALL_BLOOD_BANKS = "all";

// The dashboard asks for every configured row in one go. The service falls back
// to a page of 50 when no limit is given, which was tolerable while each
// facility had its own card and invisible once those cards are summed: a
// national total missing a page of facilities reads as a real shortage.
const DASHBOARD_LIMIT = 1000;

class DashboardInventories extends React.Component {
  state = {
    isLoading: false,
    data: {},
    page: 1,
    limit: 10,
    search_text: "",
    selectedData: {},
    delete_value: "",
    error: {},
    csvData: [],
    user: {},

    filterStock: null,
    stock: null,
    isNational: false,
};

  componentDidMount = async () => {
    const user = await getStorage();
    const isNational = await hasNationalInventory();

    await this.setState({ user, isNational });

    if (user.stock && !isNational) {
      this.setState({
        stock: {
          label: user.stock,
          value: user.stock,
        },
      });
    } else {
      // An account that is not pinned to a blood bank, or one holding the
      // national inventory role, opens on the whole country. The filter
      // said nothing about that before, so national figures read as though
      // they belonged to one facility.
      this.setState({
        filterStock: this.allBloodBanksOption(),
      });
    }

    this.getData(true, {
      status: "INSTOCK",
    });
  };

  // Whether this account is looking at every blood bank or just its own.
  // Two different reasons land here - no blood bank at all, or the national
  // inventory role - and every part of the screen has to agree on it.
  isCountryWide = () => this.state.isNational || !this.state.user.stock;

  allBloodBanksOption = () => ({
    label: language[this.props.defaultLanguage].all_hospitals,
    value: ALL_BLOOD_BANKS,
  });

  // The facility the cards are currently scoped to, or undefined for the whole
  // country. The drill-down reads this too: a list scoped differently from the
  // card contradicts the number that was clicked.
  selectedFacility = () => {
    const { filterStock } = this.state;

    if (!filterStock || filterStock.value === ALL_BLOOD_BANKS) return undefined;

    return filterStock.value;
  };

  onChangeText(field, e) {
    let { error } = this.state;

    let value = e && e?.target ? e?.target?.value : e;

    delete error[field];

    this.setState({
      [field]: value,
      error,
    });

    if (field === "filterStock") {
      this.getData(true, {
        stock:
          value && value.value !== ALL_BLOOD_BANKS ? value.value : undefined,
      });
    }
  }

  // One card per product type and blood group, summed over every facility
  // holding it. The service keys a row on (stock, productType, bloodGroup), so
  // an account seeing the whole country got one card per facility, each
  // labelled with nothing but the blood group and no way to tell them apart.
  //
  // The triple is deduped before anything is added up: two configured rows for
  // one facility carry the same live count and would otherwise count its units
  // twice.
  aggregateRows = (rows = []) => {
    const seen = new Set();
    const merged = new Map();

    rows.forEach((el) => {
      const { productType } = el;

      if (!productType) return;

      const stockId = el.stock?._id || el.stock;
      const productTypeId = productType._id || productType;
      const triple = `${stockId}|${productTypeId}|${el.bloodGroup}`;

      if (seen.has(triple)) return;

      seen.add(triple);

      const key = `${productTypeId}|${el.bloodGroup}`;
      const current = merged.get(key);

      if (!current) {
        merged.set(key, {
          _id: key,
          productType,
          bloodGroup: el.bloodGroup,
          currentStock: el.currentStock || 0,
          control: el.control
            ? {
                minStock: el.control.minStock || 0,
                maxStock: el.control.maxStock || 0,
              }
            : null,
        });

        return;
      }

      current.currentStock += el.currentStock || 0;

      // Thresholds add up the way the stock does: the national minimum is what
      // the facilities need between them. A facility with no control of its own
      // still contributes units, so a total can read as in stock while one of
      // them sits at zero - picking that facility in the filter is the view
      // that answers it.
      if (el.control) {
        current.control = {
          minStock:
            (current.control?.minStock || 0) + (el.control.minStock || 0),
          maxStock:
            (current.control?.maxStock || 0) + (el.control.maxStock || 0),
        };
      }
    });

    return [...merged.values()];
  };

  getData = async (isLoading, search = {}) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoading });

      const { data, count, totalRecords } = await fetchCurrentInventoriesPerProductType(
        defaultLanguage,
        { ...search, limit: DASHBOARD_LIMIT }
      );

      let group = {};

      this.aggregateRows(data).forEach((el) => {
        if (!group[el.productType.name]) {
          group[el.productType.name] = [];
        }

        group[el.productType.name].push(el);
      });

      this.setState({
        data: group,
        totalPageCount: count,
        totalRecords,
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  handleInitInventory = async () => {
    await this.validateInitInventory();
    try {
      if (Object.keys(this.state.error).length > 0) return;
      this.setState({
        isInit: true,
      });
      const { defaultLanguage } = this.props;
      const { stock } = this.state;
      await fetchAndUpdateCurrentInventoriesStockStatus(defaultLanguage, {
        stock: stock.value,
      });

      this.setState({
        isInit: false,
      });

      toastMessage(
        "success",
        language[this.props.defaultLanguage].initialize_inventory_success
      );
      this.getData(true);
      this.handleCloseModal("showInitModal");
      window.location.reload();
    } catch (error) {
      console.log(error);
      toastMessage("error", error);
    }
  };

  handleShowModal(modal, modalTitle, selectedData) {
    this.setState({
      [modal]: true,
      modalTitle,
      selectedData,
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  validateInitInventory = () => {
    let { error, stock } = this.state;

    if (!stock) {
      error.stock = language[this.props.defaultLanguage].stock_required;
    }

    this.setState({ error });
  };

  getBloodBank = async (q) => {
    try {
      this.setState({ isFetchingBloodBanks: true });
      const user = await getStorage();
      const countryWide = this.isCountryWide();

      // fetchBloodBanks has a ROLE_BLOOD_BANK gate that blocks hospital users.
      // For a facility account this dropdown only ever shows its own blood
      // bank (_id: user.stock), so make the call directly without the role
      // check. An account seeing the country needs the list unfiltered, and
      // a page of fifty would quietly hide the rest of the hospitals.
      const queryRes = await api({
        method: "GET",
        url: `${process.env.REACT_APP_BBMS_BASE_API}/bloodBank`,
        params: {
          page: 1,
          limit: countryWide ? 1000 : 50,
          _id: countryWide ? undefined : user.stock,
          q: q !== "" ? q : undefined,
        },
        headers: { Authorization: `Bearer ${user.token}` },
      });

      const raw = queryRes?.data?.data || queryRes?.data || [];
      const data = raw
        .map((el) => ({ ...el, label: el.code + "-" + el.name, value: el._id }))
        .filter(Boolean);

      this.setState({
        bloodBanks: data,
        isFetchingBloodBanks: false,
        stock: data.length === 1 ? data[0] : this.state.stock,
      });

      return data;
    } catch (error) {
      this.setState({ isFetchingBloodBanks: false });
      return [];
    }
  };

  getBloodBankFilterOptions = async (q) => {
    const data = await this.getBloodBank(q);

    // An account pinned to one blood bank and without the national role is
    // scoped to it by the service whatever this filter says, so offering
    // "all" would promise a view it cannot be given.
    if (!this.isCountryWide()) return data;

    return [this.allBloodBanksOption(), ...data];
  };

  render() {
    return (
      <>
        <div className="row">
          <div style={{ display: "flex" }}>
            <div className="explain-stock-container" style={{ flex: 1 }}>
              <div className="explain-stock-item">
                <div className="color red" />
                <div>Out of stock</div>
              </div>
              <div className="explain-stock-item">
                <div className="color yellow" />
                <div>Insufficient Stock</div>
              </div>
              <div className="explain-stock-item">
                <div className="color green" />
                <div>In stock</div>
              </div>

              <div className="explain-stock-item">
                <div className="color other" />
                <div>Over loaded</div>
              </div>
            </div>

            {/* {Object.keys(this.state.data || {}).length > 0 && (
              <div>
                <Button
                  isSubmitting={this.state.isInit}
                  text={language[this.props.defaultLanguage].refresh}
                  onPress={() =>
                    this.state.user.stock
                      ? this.handleInitInventory(this.state.user.stock)
                      : this.handleShowModal("showInitModal")
                  }
                />
              </div>
            )} */}
          </div>
          <div className="col-md-12">
            <Select
              loadOptions={(e) => this.getBloodBankFilterOptions(e)}
              placeholder={
                this.isCountryWide()
                  ? language[this.props.defaultLanguage].all_hospitals
                  : language[this.props.defaultLanguage].select
              }
              label={language[this.props.defaultLanguage].filter_blood_bank}
              helperText={
                this.isCountryWide()
                  ? language[this.props.defaultLanguage]
                      .filter_blood_bank_helper
                  : undefined
              }
              value={this.state.filterStock}
              onChange={(e) => this.onChangeText("filterStock", e)}
              error={this.state.error.filterStock}
            />
          </div>
        </div>
        {this.state.isLoading ? (
          <center>
            <Loading />
          </center>
        ) : Object.keys(this.state.data).length === 0 ? (
          <>
            <Empty
              title={
                language[this.props.defaultLanguage]
                  .initialize_inventory_description
              }
              actionButton={
                language[this.props.defaultLanguage].initialize_inventory
              }
              handleActionButton={() =>
                this.handleShowModal(
                  "showInitModal",
                  language[this.props.defaultLanguage].initialize_inventory
                )
              }
            />
          </>
        ) : (
          Object.keys(this.state.data).length > 0 && (
            <div>
              {Object.keys(this.state.data || {})?.map((k) => {
                return (
                  <div key={k}>
                    <div className="mb-3">
                      <CardHeader title={k} />
                    </div>
                    <div className="row">
                      {this.state.data[k]?.map((el, i) => {
                        // A blood group with stock but no InventoryControl has
                        // no minimum or maximum to be judged against, so it
                        // gets a neutral colour rather than falling through
                        // the chain to `false` and rendering uncoloured. The
                        // count is still shown: holding blood the dashboard
                        // stays silent about is the dangerous direction.
                        const color = !el.control
                          ? el.currentStock === 0
                            ? "red"
                            : "#6c757d"
                          : el.currentStock === 0
                          ? "red"
                          : el.currentStock <= el.control?.minStock
                          ? "#f0b807"
                          : el.control?.minStock <= el.currentStock &&
                            el.control?.maxStock >= el.currentStock
                          ? "green"
                          : el.control?.maxStock <= el.currentStock &&
                            "purple";
                        return (
                          <div key={el._id || i} className="col-6 col-md-4 mb-3">
                            <CardCount
                              count={el.currentStock}
                              title={`${
                                language[this.props.defaultLanguage].blood_group
                              }: ${el.bloodGroup}`}
                              descriptionStyles={{
                                fontWeight: "bold",
                              }}
                              color={color}
                              icon={icons.transfer}
                              onPress={() =>
                                this.handleShowModal(
                                  "showProductModal",
                                  el?.productType?.name + " " + el?.bloodGroup,
                                  el
                                )
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        <Modal
          show={this.state.showInitModal}
          title={this.state.modalTitle}
          handleClose={() => this.handleCloseModal("showInitModal")}
        >
          <div className="card-body">
            <div className="row">
              <div className="col-md-12">
                <Select
                  defaultOptions
                  loadOptions={(e) => this.getBloodBank(e)}
                  placeholder={language[this.props.defaultLanguage].select}
                  label={language[this.props.defaultLanguage].blood_bank}
                  required
                  value={this.state.stock}
                  onChange={(e) => this.onChangeText("stock", e)}
                  error={this.state.error.stock}
                />
              </div>
              <div className="modal-footer">
                <Button
                  text={language[this.props.defaultLanguage].submit}
                  isSubmitting={this.state.isInit}
                  onPress={this.handleInitInventory.bind(this)}
                />
              </div>
            </div>
          </div>
        </Modal>

        <Modal
          show={this.state.showProductModal}
          title={this.state.modalTitle}
          handleClose={() => this.handleCloseModal("showProductModal")}
          size="lg"
        >
          {this.state.selectedData && this.state.selectedData.productType && (
            <div
              className="card-body"
              style={{ maxHeight: "80vh", overflowY: "auto" }}
            >
              <ViewProducts
                bloodGroup={this.state.selectedData.bloodGroup}
                productIds={[this.state.selectedData.productType._id]}
                stock={this.selectedFacility()}
                limit={10}
              />
            </div>
          )}
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  const { filters } = state.Filters;
  return {
    defaultLanguage,
    filters,
  };
};

export default connect(mapStateToProps)(DashboardInventories);
